import React, { forwardRef } from "react";

const Timeline = forwardRef<HTMLDivElement, any>(function Timeline(
  {
    duration,
    edits,
    current,
    setSelectedIdx,
    selectedIdx,
    FPS,
    zoom,
    setZoom,
    seekTo,
    secToPx,
    startDrag,
    refresh
  },
  ref
) {
  const frames = Math.floor(duration * FPS);

  const getTimelineDiv = () => {
    if (!ref) return null;
    if (typeof ref === "function") return null;
    return (ref as React.MutableRefObject<HTMLDivElement | null>).current;
  };

  const handleCursorDrag = (e: any) => {
    e.preventDefault();
    const timelineDiv = getTimelineDiv();
    if (!timelineDiv) return;
    const timelineRect = timelineDiv.getBoundingClientRect();
    const pxToSec = (duration / timelineRect.width);

    const onMove = (moveEvent: any) => {
      const x = moveEvent.clientX - timelineRect.left;
      let t = Math.max(0, Math.min(duration, x * pxToSec));
      t = Math.round(t * FPS) / FPS;
      seekTo(t);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  return (
    <div>
      <div className="flex gap-3 mb-3 items-center">
        <button className="btn-dark-secondary" onClick={() => setZoom((z: any) => Math.min(z * 2, 16))}>Zoom +</button>
        <button className="btn-dark-secondary" onClick={() => setZoom((z: any) => Math.max(z / 2, 1))}>Zoom -</button>
        <span className="text-sm text-brand_gray font-medium">Zoom: x{zoom}</span>
      </div>
      <div
        ref={ref}
        className="relative h-20 bg-gray-900 rounded-xl mb-6 border border-brand_200 overflow-x-auto no-scrollbar"
        style={{ width: "100%", maxWidth: "100%" }}
      >
        <div
          className="timeline-track min-w-0"
          style={{
            position: "relative",
            height: "100%",
            width: `${zoom * 100}%`,
            minWidth: "100%",
            transition: "width 0.2s cubic-bezier(.4,2,.4,1)"
          }}
        >
          {/* Ticks */}
          {[...Array(frames)].map((_, i) => (
            <div
              key={i}
              className="absolute"
              style={{
                left: secToPx(i / FPS),
                width: 1,
                height: i % FPS === 0 ? 18 : 8,
                background: i % FPS === 0 ? "#ffd700" : "#fff2",
                top: 0,
                opacity: i % 5 === 0 ? 1 : 0.6
              }}
            />
          ))}
          {/* Segments */}
          {edits.map((seg: any, idx: any) => (
            <div
              key={idx}
              className={`absolute top-6 py-5 cursor-pointer transition-all duration-100 z-20`}
              style={{
                left: secToPx(seg.start),
                width: Math.max(secToPx(seg.end) - secToPx(seg.start), 8),
                height: 32,
                background:
                  seg.speed === "cut"
                    ? "#e91e63"
                    : seg.speed !== 1
                    ? "rgb(var(--color-brand_main))"
                    : "rgba(67,56,202,0.5)",
                border:
                  selectedIdx === idx ? "3px solid rgb(var(--color-brand_focus))" : "2px solid #333",
                borderRadius: "8px",
              }}
              onClick={() => setSelectedIdx(idx)}
              onMouseDown={startDrag(idx)}
            >
              {/* Resize handles */}
              <div
                className="absolute left-0 top-0 w-2 h-full cursor-ew-resize"
                style={{ background: "rgba(255,255,255,0.04)" }}
                onMouseDown={e => {
                  e.stopPropagation();
                  startDrag(idx, "start")(e);
                }}
              />
              <div
                className="absolute right-0 top-0 w-2 h-full cursor-ew-resize"
                style={{ background: "rgba(255,255,255,0.04)" }}
                onMouseDown={e => {
                  e.stopPropagation();
                  startDrag(idx, "end")(e);
                }}
              />
              <span
                className="absolute left-3 top-2 text-xs font-bold bg-brand_50 text-brand_main rounded px-2 py-1 shadow"
              >
                {seg.speed === "cut"
                  ? "CUT"
                  : seg.speed && seg.speed !== 1
                  ? `x${seg.speed}`
                  : "Normal"}
              </span>
            </div>
          ))}
          {/* Curseur rouge draggable */}
          <div
            className="absolute top-5"
            style={{
              left: secToPx(current) - 2,
              width: 4,
              height: 36,
              background: "#e91e63",
              zIndex: 50,
              cursor: "ew-resize",
              userSelect: "none"
            }}
            onMouseDown={handleCursorDrag}
          />
        </div>
      </div>
    </div>
  );
});

export default Timeline;